import { useEffect, useMemo, useState } from "react";

import { type BaseViewHandle } from "../BaseViewHandle";
import { type HydratedColumnDef, type HydratedViewColumnDefs } from "../useHydratedColumnDefs";

function existingColumnNames(columnDefs: HydratedViewColumnDefs): Set<string> {
	return new Set(columnDefs.allColumns.map((column) => column.columnDef.name));
}

const SQLITE_RESERVED_KEYWORDS = new Set(
	[
		"ABORT",
		"ACTION",
		"ADD",
		"AFTER",
		"ALL",
		"ALTER",
		"ALWAYS",
		"ANALYZE",
		"AND",
		"AS",
		"ASC",
		"ATTACH",
		"AUTOINCREMENT",
		"BEFORE",
		"BEGIN",
		"BETWEEN",
		"BY",
		"CASCADE",
		"CASE",
		"CAST",
		"CHECK",
		"COLLATE",
		"COLUMN",
		"COMMIT",
		"CONFLICT",
		"CONSTRAINT",
		"CREATE",
		"CROSS",
		"CURRENT",
		"CURRENT_DATE",
		"CURRENT_TIME",
		"CURRENT_TIMESTAMP",
		"DATABASE",
		"DEFAULT",
		"DEFERRABLE",
		"DEFERRED",
		"DELETE",
		"DESC",
		"DETACH",
		"DISTINCT",
		"DO",
		"DROP",
		"EACH",
		"ELSE",
		"END",
		"ESCAPE",
		"EXCEPT",
		"EXCLUDE",
		"EXCLUSIVE",
		"EXISTS",
		"EXPLAIN",
		"FAIL",
		"FILTER",
		"FIRST",
		"FOLLOWING",
		"FOR",
		"FOREIGN",
		"FROM",
		"FULL",
		"GENERATED",
		"GLOB",
		"GROUP",
		"GROUPS",
		"HAVING",
		"IF",
		"IGNORE",
		"IMMEDIATE",
		"IN",
		"INDEX",
		"INDEXED",
		"INITIALLY",
		"INNER",
		"INSERT",
		"INSTEAD",
		"INTERSECT",
		"INTO",
		"IS",
		"ISNULL",
		"JOIN",
		"KEY",
		"LAST",
		"LEFT",
		"LIKE",
		"LIMIT",
		"MATCH",
		"MATERIALIZED",
		"NATURAL",
		"NO",
		"NOT",
		"NOTHING",
		"NOTNULL",
		"NULL",
		"NULLS",
		"OF",
		"OFFSET",
		"ON",
		"OR",
		"ORDER",
		"OTHERS",
		"OUTER",
		"OVER",
		"PARTITION",
		"PLAN",
		"PRAGMA",
		"PRECEDING",
		"PRIMARY",
		"QUERY",
		"RAISE",
		"RANGE",
		"RECURSIVE",
		"REFERENCES",
		"REGEXP",
		"REINDEX",
		"RELEASE",
		"RENAME",
		"REPLACE",
		"RESTRICT",
		"RETURNING",
		"RIGHT",
		"ROLLBACK",
		"ROW",
		"ROWS",
		"SAVEPOINT",
		"SELECT",
		"SET",
		"TABLE",
		"TEMP",
		"TEMPORARY",
		"THEN",
		"TIES",
		"TO",
		"TRANSACTION",
		"TRIGGER",
		"UNBOUNDED",
		"UNION",
		"UNIQUE",
		"UPDATE",
		"USING",
		"VACUUM",
		"VALUES",
		"VIEW",
		"VIRTUAL",
		"WHEN",
		"WHERE",
		"WINDOW",
		"WITH",
		"WITHOUT",
	].map((keyword) => keyword.toLowerCase())
);

function headerToAccessorKey(input: string, existingKeys: Set<string>): string {
	let formattedString = input
		.toLowerCase()
		.trim()
		// We are not ready to support Unicode yet. Some say we will use a regex like the following:
		//
		//   - \u2000-\u206F is the Unicode general punctuation block.
		//   - \u2E00-\u2E7F is the Unicode supplemental punctuation block.
		//   - \p{P} is the Unicode punctuation block, but which does not include some ASCII
		//     punctuation like `$`.
		//   - All the ASCII punctuation characters. Some of them from above are not caught.
		//
		//
		// .replace(/[\p{P}\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,\-.\/:;<=>?@\[\]^_`{|}~]+/gu, "_");
		.replace(/[^a-zA-Z0-9]+/gu, "_");

	// SQLite does not allow:
	if (
		// Column names that use reserved keywords.
		SQLITE_RESERVED_KEYWORDS.has(formattedString) ||
		// Column names that are empty.
		formattedString.length === 0 ||
		// Column names that start with a number.
		formattedString.match(/^[0-9]/)
	) {
		formattedString = `_${formattedString}`;
	}

	let uniqueString = formattedString;
	let counter = 1;

	while (existingKeys.has(uniqueString)) {
		uniqueString = `${formattedString}${counter}`;
		counter++;
	}

	return uniqueString;
}

export const useRenameColumnState = (
	viewHandle: BaseViewHandle,
	column: HydratedColumnDef | undefined
) => {
	const name = column?.columnDef?.name;
	const header = column?.columnDef?.header;

	const currentHeaderValue = header ?? name;
	const [internalHeaderValue, setInternalHeaderValue] = useState(header ?? name);
	useEffect(() => setInternalHeaderValue(header ?? name), [header, name]);
	const headerChanged = internalHeaderValue?.trim() !== currentHeaderValue?.trim();

	const newColumnName = useMemo(() => {
		if (!internalHeaderValue || !headerChanged) {
			return undefined;
		}
		return headerToAccessorKey(internalHeaderValue, existingColumnNames(viewHandle.columns));
	}, [internalHeaderValue, headerChanged, viewHandle.columns]);

	return {
		currentHeaderValue,
		internalHeaderValue,
		setInternalHeaderValue,
		headerChanged,
		newColumnName,
	};
};
